import { useState } from 'react';
import {
    Plus, Search, Phone, Mail, MapPin,
    X, Edit2, Trash2, Loader2, User
} from 'lucide-react';
import {
    useSurveyors,
    useCreateSurveyor,
    useUpdateSurveyor,
    useDeleteSurveyor
} from '../../Hooks/useSurveyors';
import toast from 'react-hot-toast';
import { canAccess } from '../../utils/permissions';

export default function Surveyors() {
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSurveyors, setSelectedSurveyors] = useState([]);
    const [editingSurveyor, setEditingSurveyor] = useState(null);

    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        phone: '',
        password: '',
        location: '',
        specialization: '',
        license_number: '',
        experience_years: '' 
    });

    const userData = JSON.parse(localStorage.getItem("user_data"));
    const CURRENT_COMPANY_ID = userData.id;

    const { data: surveyors = [], isLoading, isError } = useSurveyors();
    const createMutation = useCreateSurveyor();
    const updateMutation = useUpdateSurveyor();
    const deleteMutation = useDeleteSurveyor();

    const filteredSurveyors = surveyors.filter(s =>
        s.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.phone?.includes(searchTerm)
    );

    const toggleSurveyor = (id) => {
        setSelectedSurveyors(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const resetForm = () => {
        setFormData({
            full_name: '',
            email: '',
            phone: '',
            password: '',
            location: '',
            specialization: '',
            license_number: '',
            experience_years: ''
        });
    };

    const handleCreate = () => {
        if (!formData.full_name?.trim()) return toast.error('Full Name is required');
        if (!formData.email?.trim()) return toast.error('Email is required');
        if (!formData.phone?.trim()) return toast.error('Phone is required');

        createMutation.mutate({
            company_id: CURRENT_COMPANY_ID,
            full_name: formData.full_name,
            email: formData.email,
            phone: formData.phone,
            password: formData.password || undefined,
            location: formData.location || null,
            specialization: formData.specialization || null,
            license_number: formData.license_number || null,
            experience_years: formData.experience_years ? Number(formData.experience_years) : null,
        }, {
            onSuccess: () => {
                setShowAddModal(false);
                resetForm();
            }
        });
    };

    const handleUpdate = () => {
        updateMutation.mutate({
            id: editingSurveyor.id,
            company_id: CURRENT_COMPANY_ID, // إذا كان مطلوب في التحديث
            full_name: formData.full_name,
            email: formData.email,
            phone: formData.phone,
            location: formData.location || null,
            specialization: formData.specialization || null,
            license_number: formData.license_number || null,
            experience_years: formData.experience_years ? Number(formData.experience_years) : null,
        }, {
            onSuccess: () => {
                setShowEditModal(false);
                resetForm();
                setEditingSurveyor(null);
            }
        });
    };

    const openEditModal = (surveyor) => {
        setEditingSurveyor(surveyor);
        setFormData({
            full_name: surveyor.full_name || '',
            email: surveyor.email || '',
            phone: surveyor.phone || '',
            password: '',
            location: surveyor.location || '',
            specialization: surveyor.specialization || '',
            license_number: surveyor.license_number || '',
            experience_years: surveyor.experience_years || ''
        });
        setShowEditModal(true);
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this surveyor?')) {
            deleteMutation.mutate(id);
        }
    };

    if (isLoading) return <div className="p-8 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>;
    if (isError) return <div className="p-8 text-center text-red-600">Failed to load surveyors</div>;

    if (!canAccess("view_surveyors")) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
                <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
                    <User className="w-10 h-10 text-red-600" />
                </div>
                <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Access Restricted</h2>
                <p className="text-gray-500 max-w-md font-medium">
                    You don't have the required permissions to view surveyors.
                </p>
            </div>
        );
    }

    return (
        <section className="min-h-screen text-sm">
            <div className="bg-white border-b border-gray-200 px-4 py-4">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Surveyors</h1>
                        <p className="text-xs text-gray-600 mt-1">Manage your field team</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search surveyors..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2.5 w-72 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 text-sm bg-white"
                            />
                        </div>
                        {canAccess('add_surveyor') && (
                            <button
                                onClick={() => { resetForm(); setShowAddModal(true); }}
                                className="px-4 py-2.5 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-md hover:shadow-lg transition"
                            >
                                <Plus className="w-4 h-4" />
                                Add Surveyor
                            </button>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-xs font-medium text-gray-600">Total Surveyors</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{surveyors.length}</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-xs font-medium text-gray-600">Active</p>
                        <p className="text-2xl font-bold text-emerald-600 mt-1">
                            {surveyors.filter(s => s.status === 'active').length}
                        </p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-xs font-medium text-gray-600">Pending</p>
                        <p className="text-2xl font-bold text-amber-600 mt-1">
                            {surveyors.filter(s => s.status !== 'active').length}
                        </p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-xs font-medium text-gray-600">Last Added</p>
                        <p className="text-xs font-medium text-gray-900 mt-1">Recently</p>
                    </div>
                </div>
            </div>

            <div className="px-4 py-5">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {filteredSurveyors.length === 0 ? (
                        <div className="col-span-full text-center py-12 text-gray-500">
                            <div className="bg-gray-200 border-2 border-dashed rounded-xl w-24 h-24 mx-auto mb-4" />
                            <p className="text-lg">No surveyors found</p>
                        </div>
                    ) : (
                        filteredSurveyors.map((surveyor) => (
                            <div key={surveyor.id} className="bg-white rounded-xl shadow-sm border border-gray-200 hover:border-teal-300 transition-all">
                                <div className="p-5">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-start gap-3">
                                            <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-teal-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                                                {surveyor.full_name.split(' ').map(n => n[0].toUpperCase()).join('')}
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-gray-900">{surveyor.full_name}</h3>
                                                <p className="text-xs text-gray-600 capitalize">{surveyor.specialization || 'General Surveyor'}</p>
                                            </div>
                                        </div>
                                        {/* <button onClick={() => toggleSurveyor(surveyor.id)} className="p-1 hover:bg-gray-100 rounded-lg">
                                            {selectedSurveyors.includes(surveyor.id) ?
                                                <CheckSquare className="w-5 h-5 text-teal-600" /> :
                                                <Square className="w-5 h-5 text-gray-400" />
                                            }
                                        </button> */}
                                    </div>

                                    <div className="space-y-2 text-xs mb-4">
                                        <div className="flex items-center gap-2 text-gray-600">
                                            <Mail className="w-4 h-4" />
                                            <span className="truncate">{surveyor.email}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-600">
                                            <Phone className="w-4 h-4" />
                                            <span>{surveyor.phone}</span>
                                        </div>
                                        {surveyor.location && (
                                            <div className="flex items-center gap-2 text-gray-600">
                                                <MapPin className="w-4 h-4" />
                                                <span>{surveyor.location}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                                        <div className="flex gap-2">
                                            {canAccess('update_surveyor') && (
                                                <button onClick={() => openEditModal(surveyor)} className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition">
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                            )}
                                            {canAccess('delete_surveyor') && (
                                                <button onClick={() => handleDelete(surveyor.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${surveyor.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}`}>
                                            {surveyor.status || 'Pending'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {(showAddModal || showEditModal) && (
                <>
                    <div className="fixed inset-0 bg-black/50 z-[80]" onClick={() => { setShowAddModal(false); setShowEditModal(false); resetForm(); }} />
                    <div className="fixed inset-4 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:max-w-2xl w-full bg-white rounded-2xl shadow-2xl z-[100] overflow-hidden">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-900">
                                    {showEditModal ? 'Edit Surveyor' : 'Add New Surveyor'}
                                </h2>
                                <button onClick={() => { setShowAddModal(false); setShowEditModal(false); resetForm(); }} className="p-2 hover:bg-gray-100 rounded-lg transition">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Full Name *</label>
                                <input type="text" value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm" placeholder="Ahmed Ali" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Email *</label>
                                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Phone *</label>
                                <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm" />
                            </div>
                            {showAddModal && (
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Password (optional)</label>
                                    <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Leave empty to auto-generate" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm" />
                                </div>
                            )}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
                                <input type="text" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="e.g. Miami, Florida" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Specialization</label>
                                <input type="text" value={formData.specialization} onChange={(e) => setFormData({ ...formData, specialization: e.target.value })} placeholder="e.g. Hull & Engine Survey" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">License Number</label>
                                <input type="text" value={formData.license_number} onChange={(e) => setFormData({ ...formData, license_number: e.target.value })} placeholder="LIC-12345" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Years of Experience</label>
                                <input type="number" min="0" value={formData.experience_years} onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })} placeholder="10" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm" />
                            </div>
                        </div>

                        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                            <button onClick={() => { setShowAddModal(false); setShowEditModal(false); resetForm(); }} className="px-6 py-3 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition">
                                Cancel
                            </button>
                            {(() => {
                                const canAdd = canAccess('add_surveyor');
                                const canUpdate = canAccess('update_surveyor');
                                const hasPermission = showEditModal ? canUpdate : canAdd;

                                if (hasPermission) {
                                    return (
                                        <button
                                            onClick={showEditModal ? handleUpdate : handleCreate}
                                            disabled={createMutation.isPending || updateMutation.isPending}
                                            className="px-8 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg text-sm font-bold shadow-md hover:shadow-lg disabled:opacity-70 flex items-center gap-2 transition"
                                        >
                                            {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="w-5 h-5 animate-spin" />}
                                            {createMutation.isPending || updateMutation.isPending ? 'Saving...' : (showEditModal ? 'Update' : 'Create')}
                                        </button>
                                    );
                                } else {
                                    return (
                                        <div className="text-sm text-gray-500 flex items-center">
                                            You don't have permission to {showEditModal ? 'update' : 'add'} surveyors.
                                        </div>
                                    );
                                }
                            })()}
                        </div>
                    </div>
                </>
            )}
        </section>
    );
}